"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get AiModel () {
        return AiModel;
    },
    get AiSettingsSchema () {
        return AiSettingsSchema;
    },
    get AiSettingsSchemaWithDefault () {
        return AiSettingsSchemaWithDefault;
    },
    get reviewCycleInsightsSettings () {
        return reviewCycleInsightsSettings;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const AiModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    embedding: _zod.z.string(),
    created_at: _zod.z.date(),
    updated_at: _zod.z.date()
});
const AiAuthoringAssistantPreferredWritingStyle = _zod.z.enum([
    "casual",
    "businessProfessional",
    "businessCasual",
    "formal"
]);
const authoringAssistantEnabled = _zod.z.boolean();
const formQuestionAssistantEnabled = _zod.z.boolean();
const authoringAssistantSettings = _zod.z.object({
    preferredWritingStyle: AiAuthoringAssistantPreferredWritingStyle,
    disallowedWords: _zod.z.array(_zod.z.string())
});
const reviewSummarizationEnabled = _zod.z.boolean();
const reviewSummarizationSettings = _zod.z.object({
    strengths: _zod.z.boolean(),
    quotes: _zod.z.boolean(),
    actions: _zod.z.boolean(),
    tone: _zod.z.boolean(),
    opinions: _zod.z.boolean(),
    summary: _zod.z.boolean()
});
const reviewCycleInsightsEnabled = _zod.z.boolean();
const reviewCycleInsightsSettings = _zod.z.object({
    summaryCompleteNotification: _zod.z.boolean(),
    reviewToneDistribution: _zod.z.boolean(),
    highlightedReviews: _zod.z.boolean(),
    keyStrengthsAndOpportunities: _zod.z.boolean(),
    commonThemesPositiveReviews: _zod.z.boolean(),
    commonThemesNegativeReviews: _zod.z.boolean(),
    recurringActionItems: _zod.z.boolean(),
    disconnectsAnalysis: _zod.z.boolean()
});
const engagementAnalysisEnabled = _zod.z.boolean();
const engagementCohortInsightsEnabled = _zod.z.boolean();
const meetingWorkspaceInsightsEnabled = _zod.z.boolean();
const meetingWorkspaceInsightsSettings = _zod.z.object({
    progressAndGrowth: _zod.z.boolean(),
    keyAccomplishmentsAndWins: _zod.z.boolean(),
    developmentAreasAndGrowthOpportunities: _zod.z.boolean(),
    notableDiscussionsAndDecisions: _zod.z.boolean(),
    actionItemsAnalysis: _zod.z.boolean()
});
const surveyInsightsEnabled = _zod.z.boolean();
const surveyInsightsSettings = _zod.z.object({
    sentimentAnalysis: _zod.z.boolean(),
    keyThemesAndPatterns: _zod.z.boolean(),
    outliersAndNotableResponses: _zod.z.boolean(),
    actionableInsightsAndRecommendations: _zod.z.boolean(),
    demographicInsights: _zod.z.boolean()
});
const conversationalAiEnabled = _zod.z.boolean();
const conversationalAiSettings = _zod.z.object({
    askHrEnabled: _zod.z.boolean(),
    goalCreationEnabled: _zod.z.boolean()
});
const AiSettingsSchema = _zod.z.object({
    /* AI authoring assistant */ authoringAssistantEnabled,
    formQuestionAssistantEnabled,
    authoringAssistantSettings,
    /* Review summarization assistant */ reviewSummarizationEnabled,
    reviewSummarizationSettings,
    /* Review cycle insights assistant */ reviewCycleInsightsEnabled,
    reviewCycleInsightsSettings,
    /* Engagement analysis assistant */ engagementAnalysisEnabled,
    engagementCohortInsightsEnabled,
    /* Meeting workspace insights assistant */ meetingWorkspaceInsightsEnabled,
    meetingWorkspaceInsightsSettings,
    /* Survey insights assistant */ surveyInsightsEnabled,
    surveyInsightsSettings,
    /* Conversational AI assistant */ conversationalAiSettings
});
const AiSettingsSchemaWithDefault = _zod.z.object({
    /* AI authoring assistant */ authoringAssistantEnabled: authoringAssistantEnabled.default(false),
    formQuestionAssistantEnabled: formQuestionAssistantEnabled.default(false),
    authoringAssistantSettings: authoringAssistantSettings.default({
        preferredWritingStyle: "businessProfessional",
        disallowedWords: []
    }),
    /* Review summarization assistant */ reviewSummarizationEnabled: reviewSummarizationEnabled.default(false),
    reviewSummarizationSettings: reviewSummarizationSettings.default({
        strengths: true,
        quotes: true,
        actions: true,
        tone: true,
        opinions: true,
        summary: true
    }),
    /* Review cycle insights assistant */ reviewCycleInsightsEnabled: reviewCycleInsightsEnabled.default(false),
    reviewCycleInsightsSettings: reviewCycleInsightsSettings.default({
        summaryCompleteNotification: false,
        reviewToneDistribution: true,
        highlightedReviews: true,
        keyStrengthsAndOpportunities: true,
        commonThemesPositiveReviews: true,
        commonThemesNegativeReviews: true,
        recurringActionItems: true,
        disconnectsAnalysis: true
    }),
    /* Engagement analysis assistant */ engagementAnalysisEnabled: engagementAnalysisEnabled.default(true),
    engagementCohortInsightsEnabled: engagementCohortInsightsEnabled.default(true),
    /* Meeting workspace insights assistant */ meetingWorkspaceInsightsEnabled: meetingWorkspaceInsightsEnabled.default(false),
    meetingWorkspaceInsightsSettings: meetingWorkspaceInsightsSettings.default({
        progressAndGrowth: true,
        keyAccomplishmentsAndWins: true,
        developmentAreasAndGrowthOpportunities: true,
        notableDiscussionsAndDecisions: true,
        actionItemsAnalysis: true
    }),
    /* Survey insights assistant */ surveyInsightsEnabled: surveyInsightsEnabled.default(false),
    surveyInsightsSettings: surveyInsightsSettings.default({
        sentimentAnalysis: true,
        keyThemesAndPatterns: true,
        outliersAndNotableResponses: true,
        actionableInsightsAndRecommendations: true,
        demographicInsights: true
    }),
    /* Conversational AI assistant */ conversationalAiEnabled: conversationalAiEnabled.optional(),
    conversationalAiSettings: conversationalAiSettings.optional()
}).transform((data)=>{
    const { conversationalAiEnabled: legacyEnabled, ...rest } = data;
    // Determine final conversationalAiSettings value
    let conversationalAiSettings = data.conversationalAiSettings;
    // Migration: If legacy field exists but new settings don't, migrate the value
    if (legacyEnabled !== undefined && conversationalAiSettings === undefined) {
        conversationalAiSettings = {
            askHrEnabled: legacyEnabled,
            goalCreationEnabled: false
        };
    }
    // Apply defaults if still undefined
    if (conversationalAiSettings === undefined) {
        conversationalAiSettings = {
            askHrEnabled: false,
            goalCreationEnabled: false
        };
    }
    return {
        ...rest,
        conversationalAiSettings
    };
}).default({
    authoringAssistantEnabled: false,
    formQuestionAssistantEnabled: false,
    authoringAssistantSettings: {
        preferredWritingStyle: "businessCasual",
        disallowedWords: []
    },
    reviewSummarizationEnabled: false,
    reviewSummarizationSettings: {
        strengths: true,
        quotes: true,
        actions: true,
        tone: true,
        opinions: true,
        summary: true
    },
    reviewCycleInsightsEnabled: false,
    reviewCycleInsightsSettings: {
        summaryCompleteNotification: false,
        reviewToneDistribution: true,
        highlightedReviews: true,
        keyStrengthsAndOpportunities: true,
        commonThemesPositiveReviews: true,
        commonThemesNegativeReviews: true,
        recurringActionItems: true,
        disconnectsAnalysis: true
    },
    engagementAnalysisEnabled: true,
    engagementCohortInsightsEnabled: true,
    meetingWorkspaceInsightsEnabled: false,
    meetingWorkspaceInsightsSettings: {
        progressAndGrowth: true,
        keyAccomplishmentsAndWins: true,
        developmentAreasAndGrowthOpportunities: true,
        notableDiscussionsAndDecisions: true,
        actionItemsAnalysis: true
    },
    surveyInsightsEnabled: false,
    surveyInsightsSettings: {
        sentimentAnalysis: true,
        keyThemesAndPatterns: true,
        outliersAndNotableResponses: true,
        actionableInsightsAndRecommendations: true,
        demographicInsights: true
    },
    conversationalAiSettings: {
        askHrEnabled: false,
        goalCreationEnabled: false
    }
});
